import { useState, type CSSProperties } from 'react';
import { App, Button, DatePicker, Dropdown, Flex, Input, Space, Typography } from 'antd';
import type { ButtonProps, MenuProps } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { dec } from '@app/shared';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { HotkeyButton, HotkeyTip } from '@/shared/ui/hotkey';
import { useUnsavedClose } from '@/shared/ui';
import { INVALID_INPUTS_HINT, InvalidInputsContext, MoneyInput, commitActiveField, useInvalidInputs } from '@/shared/ui/inputs';
import { useHotkeyActions } from '@/shared/lib/hotkeys';
import { useBreakpoint, useLatest } from '@/shared/lib/hooks';
import { CounterpartySelect } from '@/entities/counterparty';
import { useListDocumentsQuery } from '@/entities/document';
import type { PaymentDto } from '@/entities/payment';
import { useCreateOffsetMutation, useDeletePaymentMutation, useGetPaymentQuery, useUpdatePaymentMutation } from '@/entities/payment';
import { usePaymentSaveWithPreview, PaymentPreviewConsequencesDialog } from '@/features/payment/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { AllocationGrid, AllocationTotals, rowsFromDocuments, sumAmounts, toManualAllocations } from '@/features/payment/allocation-grid';
import type { AllocationRow } from '@/features/payment/allocation-grid';
import { useStoreContext } from '@/shared/lib/store-context';
import { formatDate, todayInTz } from '@/shared/lib/date';
import { translateApiError } from '@/shared/api/errorToMessage';
import { getOffsetBlockReason } from './model';
import './PaymentWindow.mobile.css';

/** Телефон: поля формы — один столбец на всю ширину; пункты меню «⋯» — высотой под палец. */
const MOBILE_FIELD: CSSProperties = { width: '100%', minWidth: 0 };
const MOBILE_MENU_ITEM: CSSProperties = { minHeight: 44, display: 'flex', alignItems: 'center' };

export interface PaymentWindowProps {
  id?: string;
  counterpartyId?: string;
  onClose: () => void;
}

/** §7: взаимозачёт — единственный сценарий, оставшийся в этом окне после перехода оплат/возвратов
 * денег на кассовые ордера (ПКО/РКО, `widgets/cash-order-window`). Разнесение всегда ручное на
 * обеих сторонах — у взаимозачёта нет направления и нет авто-FIFO (`POST /payments/offset`
 * требует, чтобы обе стороны в сумме сходились с суммой взаимозачёта). */
export function PaymentWindow({ id, counterpartyId: initialCounterpartyId, onClose }: PaymentWindowProps) {
  const { message, modal } = App.useApp();
  const { isMobile } = useBreakpoint();
  const labelSize = isMobile ? 13 : 12;
  const ctx = useStoreContext();
  const isNew = !id;

  const {
    data: existing,
    isFetching,
    isLoading: isLoadingExisting,
    refetch: refetchExisting,
  } = useGetPaymentQuery(id ?? '', { skip: !id });
  const [createOffset, { isLoading: isCreatingOffset }] = useCreateOffsetMutation();
  const [updatePayment, { isLoading: isUpdating }] = useUpdatePaymentMutation();
  const [deletePayment, { isLoading: isDeleting }] = useDeletePaymentMutation();
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = usePaymentSaveWithPreview();

  const [counterpartyId, setCounterpartyId] = useState(initialCounterpartyId);
  const [date, setDate] = useState(todayInTz(ctx.timezone));
  const [amount, setAmount] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [receivableRows, setReceivableRows] = useState<AllocationRow[]>([]);
  const [payableRows, setPayableRows] = useState<AllocationRow[]>([]);
  // Несохранённые изменения: без этого `Esc` закрыл бы окно вместе с введённым.
  const [dirty, setDirty] = useState(false);

  function seedFrom(d: PaymentDto) {
    setDirty(false);
    setCounterpartyId(d.counterpartyId);
    setDate(d.payDate.slice(0, 10));
    setAmount(d.amount);
    setComment(d.comment ?? '');
  }

  // Загруженный платёж → заполнить форму один раз (render-phase, не эффект).
  const [seededId, setSeededId] = useState<string | undefined>(undefined);
  if (existing && existing.id !== seededId) {
    setSeededId(existing.id);
    seedFrom(existing);
  }

  /** Кнопки диалогов подтверждения: на телефоне — 44px; на десктопе свойства те же, что были. */
  function confirmButton(base?: ButtonProps): ButtonProps | undefined {
    return isMobile ? { ...base, size: 'large' } : base;
  }

  /** §8.7 «Параллельная правка»: VERSION_CONFLICT предлагает открыть свежую версию (отбрасывая
   * локальные правки) вместо тупикового тоста — тот же приём, что и в `DocumentWindow`. */
  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Взаимозачёт изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      okButtonProps: confirmButton(),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        const result = await refetchExisting();
        if (result.data) seedFrom(result.data);
      },
    });
  }

  const { data: receivableList, isFetching: isFetchingReceivable } = useListDocumentsQuery(
    { type: 'SALE', counterpartyIds: counterpartyId ? [counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !counterpartyId },
  );
  const { data: payableList, isFetching: isFetchingPayable } = useListDocumentsQuery(
    { type: 'PURCHASE', counterpartyIds: counterpartyId ? [counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !counterpartyId },
  );

  const receivableKey = receivableList ? `r:${receivableList.items.map((i) => i.id).join(',')}` : undefined;
  const [seededReceivableKey, setSeededReceivableKey] = useState<string | undefined>(undefined);
  if (receivableKey && receivableKey !== seededReceivableKey) {
    setSeededReceivableKey(receivableKey);
    setReceivableRows(rowsFromDocuments(receivableList!.items, 'С'));
  }
  const payableKey = payableList ? `p:${payableList.items.map((i) => i.id).join(',')}` : undefined;
  const [seededPayableKey, setSeededPayableKey] = useState<string | undefined>(undefined);
  if (payableKey && payableKey !== seededPayableKey) {
    setSeededPayableKey(payableKey);
    setPayableRows(rowsFromDocuments(payableList!.items, 'П'));
  }

  const busy = isCreatingOffset || isUpdating || isDeleting || isRunningPreview;
  // Взаимозачёт открыт на существующей записи и данных ещё нет — вместо пустой формы лоадер.
  const isLoadingWindow = !!id && isLoadingExisting;
  // Сумма с опечаткой в строке разнесения уходит из поля как «пусто» — платёж сохранился бы с
  // автоматическим разнесением вместо введённого вручную.
  const invalidInputs = useInvalidInputs();
  // Причина недоступности («Укажите сумму») — для подсказки на отключённой кнопке и для сообщения,
  // если действие вызвали горячей клавишей; `null` — действие доступно.
  const formState = { counterpartyId, date, amount, receivable: receivableRows, payable: payableRows, hasInvalid: invalidInputs.hasInvalid };
  const createBlockReason = getOffsetBlockReason(formState, true);
  const saveBlockReason = getOffsetBlockReason(formState, false);

  /** Повторная проверка в самом обработчике: по клавише кнопка с `disabled` уже ничего не решает. */
  function refuse(reason: string | null): boolean {
    if (reason === null) return false;
    message.warning(reason);
    return true;
  }

  async function handleCreate() {
    if (busy || refuse(createBlockReason) || !counterpartyId || !amount) return;
    try {
      await createOffset({
        counterpartyId,
        date,
        amount,
        comment: comment || undefined,
        receivable: toManualAllocations(receivableRows),
        payable: toManualAllocations(payableRows),
      }).unwrap();
      message.success('Взаимозачёт создан');
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  async function handleSaveFields(close = false) {
    if (busy || !id || !existing || refuse(saveBlockReason)) return;
    const fields = { date, amount: amount ?? undefined, comment };
    try {
      const saved = await runWithPreview(id, 'update', existing.rowVersion, () => updatePayment({ id, body: { rowVersion: existing.rowVersion, ...fields } }).unwrap(), fields);
      if (saved) {
        setDirty(false);
        message.success('Сохранено');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleDelete() {
    if (busy || !id || !existing) return;
    modal.confirm({
      title: 'Удалить взаимозачёт?',
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: confirmButton({ danger: true }),
      cancelButtonProps: confirmButton(),
      autoFocusButton: 'cancel',
      onOk: async () => {
        try {
          const deleted = await runWithPreview(id, 'remove', existing.rowVersion, () => deletePayment({ id, body: { rowVersion: existing.rowVersion } }).unwrap());
          if (deleted) {
            message.success('Взаимозачёт удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  // ── Закрытие с вопросом и горячие клавиши ────────────────────────────────────────────────
  const { requestClose, dialog: unsavedDialog } = useUnsavedClose({
    dirty,
    onClose,
    onSave: () => void (isNew ? handleCreate() : handleSaveFields(true)),
    saveLabel: isNew ? 'Добавить' : 'Сохранить',
    saving: busy,
  });

  // Клавиша приходит раньше, чем поле «Сумма» отдало окну набранное (фиксируется на blur), а
  // замыкания биндингов — от прошлой перерисовки: сначала фиксируем активное поле, затем берём
  // обработчик из `latest`. Подробно — в DocumentWindow.
  const latest = useLatest({ handleCreate, handleSaveFields, handleDelete });
  const committed = (pick: (h: typeof latest.current) => unknown) => () => {
    if (commitActiveField().invalid) {
      message.warning(INVALID_INPUTS_HINT);
      return;
    }
    void pick(latest.current);
  };
  const actions = useHotkeyActions(
    isNew
      ? { 'editor.save': { label: 'Добавить', alwaysRun: true, disabled: createBlockReason ?? false, run: committed((h) => h.handleCreate()) } }
      : {
          'editor.save': { label: 'Сохранить', alwaysRun: true, disabled: saveBlockReason ?? false, run: committed((h) => h.handleSaveFields()) },
          'editor.archive': { label: 'Удалить', hint: false, run: committed((h) => h.handleDelete()) },
        },
  );

  return (
    <>
      <PaymentPreviewConsequencesDialog diff={pendingDiff} onResolve={resolvePending} />
      {unsavedDialog}
      <FloatingWindow
        kind="payment"
        title={isNew ? 'Новый взаимозачёт' : `Взаимозачёт от ${formatDate(date)}`}
        onClose={requestClose}
        footer={isLoadingWindow ? null : renderFooter()}
        hotkeys={isLoadingWindow ? undefined : actions.bindings}
        // На телефоне курсор при открытии не ставим: он сразу поднял бы экранную клавиатуру.
        initialFocus={isNew && !isMobile ? 'auto' : 'window'}
        loading={isLoadingWindow}
      >
        <InvalidInputsContext.Provider value={invalidInputs.api}>
          <Flex vertical gap={isMobile ? 12 : 8}>
            <Flex gap={8} wrap vertical={isMobile}>
              {/* Открыт из карточки контрагента — он уже выбран, курсор сразу в «Сумма». */}
              <Flex
                vertical
                style={isMobile ? MOBILE_FIELD : { minWidth: 220, flex: 1 }}
                data-autofocus={initialCounterpartyId || isMobile ? undefined : true}
              >
                <Typography.Text style={{ fontSize: labelSize }}>Контрагент</Typography.Text>
                <CounterpartySelect
                  value={counterpartyId}
                  current={existing?.counterparty}
                  disabled={!isNew}
                  onChange={(v) => {
                    setDirty(true);
                    setCounterpartyId(v);
                  }}
                />
              </Flex>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 140 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Дата</Typography.Text>
                <DatePicker
                  value={dayjs(date)}
                  format="DD.MM.YYYY"
                  inputReadOnly={isMobile}
                  onChange={(d) => {
                    setDirty(true);
                    setDate(d ? d.format('YYYY-MM-DD') : date);
                  }}
                />
              </Flex>
            </Flex>

            <Flex gap={8} wrap vertical={isMobile}>
              <Flex
                vertical
                className={isMobile ? 'pay-amount-field' : undefined}
                style={isMobile ? MOBILE_FIELD : { minWidth: 140 }}
                data-autofocus={initialCounterpartyId && !isMobile ? true : undefined}
              >
                <Typography.Text style={{ fontSize: labelSize }}>Сумма</Typography.Text>
                <MoneyInput
                  value={amount}
                  onChange={(v) => {
                    setDirty(true);
                    setAmount(v);
                  }}
                />
              </Flex>
              <Flex vertical style={isMobile ? MOBILE_FIELD : { minWidth: 200, flex: 1 }}>
                <Typography.Text style={{ fontSize: labelSize }}>Комментарий</Typography.Text>
                <Input
                  value={comment}
                  onChange={(e) => {
                    setDirty(true);
                    setComment(e.target.value);
                  }}
                />
              </Flex>
            </Flex>

            {/* Итоги разнесения — липкой полосой: видно, сколько разнесено с каждой стороны. */}
            {isMobile && counterpartyId ? (
              <AllocationTotals
                items={[
                  { label: 'Сумма', value: amount ? dec(amount).toFixed(2) : '0.00' },
                  { label: 'Нам должны', value: sumAmounts(receivableRows), tone: offsetSideTone(sumAmounts(receivableRows)) },
                  { label: 'Мы должны', value: sumAmounts(payableRows), tone: offsetSideTone(sumAmounts(payableRows)) },
                ]}
                note={isNew ? 'Обе стороны должны сойтись с суммой взаимозачёта' : undefined}
              />
            ) : null}

            {counterpartyId ? (
              <Flex gap={12} wrap vertical={isMobile}>
                <div style={isMobile ? { width: '100%' } : { flex: 1, minWidth: 260 }}>
                  <AllocationGrid
                    title={`Нам должны (${sumAmounts(receivableRows)})`}
                    rows={receivableRows}
                    loading={isFetchingReceivable}
                    onChange={(rows) => {
                      setDirty(true);
                      setReceivableRows(rows);
                    }}
                  />
                </div>
                <div style={isMobile ? { width: '100%' } : { flex: 1, minWidth: 260 }}>
                  <AllocationGrid
                    title={`Мы должны (${sumAmounts(payableRows)})`}
                    rows={payableRows}
                    loading={isFetchingPayable}
                    onChange={(rows) => {
                      setDirty(true);
                      setPayableRows(rows);
                    }}
                  />
                </div>
              </Flex>
            ) : null}

            {invalidInputs.hasInvalid ? (
              <Typography.Text type="danger" style={{ fontSize: labelSize }}>
                {INVALID_INPUTS_HINT}
              </Typography.Text>
            ) : null}
          </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  /** Телефон: сторона взаимозачёта подсвечивается красным, пока не сошлась с суммой. */
  function offsetSideTone(side: string): 'warn' | undefined {
    if (!isNew) return undefined;
    return amount && side === dec(amount).toFixed(2) ? undefined : 'warn';
  }

  /** Телефон: главное действие — на всю ширину, остальное — в меню «⋯»; причина недоступности
   *  видна текстом над кнопкой (подсказки по наведению на телефоне нет). */
  function renderMobileFooter() {
    const save = actions.get('editor.save');
    const del = actions.get('editor.archive');
    const items: MenuProps['items'] = [
      ...(isNew ? [] : [{ key: 'delete', label: 'Удалить', danger: true, disabled: del.disabled, onClick: () => del.run(), style: MOBILE_MENU_ITEM }]),
      { key: 'close', label: 'Закрыть', onClick: requestClose, style: MOBILE_MENU_ITEM },
    ];
    return (
      <Flex vertical gap={6}>
        {save.disabledReason ? (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {save.disabledReason}
          </Typography.Text>
        ) : null}
        <Flex gap={8}>
          <Button type="primary" size="large" loading={busy} disabled={save.disabled} onClick={save.run} style={{ flex: 1, minWidth: 0 }}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
          <Dropdown menu={{ items }} trigger={['click']} placement="topRight">
            <Button size="large" icon={<EllipsisOutlined />} aria-label="Ещё" style={{ width: 48, flex: '0 0 auto' }} />
          </Dropdown>
        </Flex>
      </Flex>
    );
  }

  function renderFooter() {
    if (isFetching && id) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
    if (isMobile) return renderMobileFooter();

    const closeButton = (
      <HotkeyTip catalogId="window.close">
        <Button onClick={requestClose}>Закрыть</Button>
      </HotkeyTip>
    );

    if (isNew) {
      return (
        <Space wrap>
          <HotkeyButton type="primary" loading={busy} action={actions.get('editor.save')}>
            Добавить
          </HotkeyButton>
          {closeButton}
        </Space>
      );
    }
    return (
      <Space wrap>
        <HotkeyButton type="primary" loading={busy} action={actions.get('editor.save')}>
          Сохранить
        </HotkeyButton>
        <HotkeyButton danger loading={busy} action={actions.get('editor.archive')}>
          Удалить
        </HotkeyButton>
        {closeButton}
      </Space>
    );
  }
}
